import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useState, type MouseEvent } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { AdTypes, GeoLevel } from '@sbt-web/network/types';
import { Button, ButtonLink } from '@sbt-web/ui';
import { formatDistance, getGeoInclusionLabel, getGeoLevel, getLabelFromGeo } from '@sbt-web/utils';
import { AdsZeroPageBlock } from '@/client/components/Adv/AdSense/listing/AdsZeroPageBlock';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { ELProps, ErrorLayout } from '@/client/components/Scaffolding/ErrorLayout';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import type { RootState } from '@/reducers/index';
import { type RadiusSearch, type SearchState, updateAdType } from '@/reducers/search';
import { useSavedSearches } from '@/shared/helpers/SavedSearches';
import { adTypesLabelsMap } from '@/tools/search/values';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { ExpandSearch } from '../ExpandSearch';
import ZrpImage from '../ZrpImage';
import ownClasses from './index.module.scss';
interface ZRStateProps {
  search: SearchState;
  filtersConfig: FiltersConfigState;
}
interface ZRDispatchProps {
  updateAdType: (type: AdTypes) => void;
}
interface ZRExternalProps {
  listingCategoryLabel?: string;
}
type ZRProps = ZRStateProps & ZRDispatchProps & ZRExternalProps;
const nextRadiusStep = (currentRadius: number): number => {
  if (currentRadius < 1000) {
    return 1000;
  } else if (currentRadius < 2500) {
    return 2500;
  } else if (currentRadius < 5000) {
    return 5000;
  } else if (currentRadius < 10000) {
    return 10000;
  } else {
    // This should not happen but I don't want the page to error.
    return 10000;
  }
};
const trackSaveSearchButton = () => {
  getOrCreatePulse()?.queueEvent({
    schema: 'http://schema.adevinta.com/events/tracker-event.json/214.json',
    type: 'Engagement',
    object: {
      id: 'save-search-zero-results',
      type: 'UIElement',
      elementType: 'banner'
    },
    target: {
      id: '0000000000000000',
      type: 'Listing'
    },
    search: {
      '@id': '0000000000000000',
      '@type': 'SearchMetadata'
    }
  });
};
const RadiusSearchExpansion = t0 => {
  const $ = _c(6);
  const {
    search: t1
  } = t0;
  const {
    radiusMeters
  } = t1;
  const [radiusExpansionLink, setRadiusExpansionLink] = useState("");
  let t2;
  let t3;
  if ($[0] !== radiusMeters) {
    t2 = () => {
      const nextStep = nextRadiusStep(radiusMeters);
      const targetUrl = new URL(window.location.href);
      targetUrl.searchParams.set("rad", nextStep.toString());
      targetUrl.hash = "";
      setRadiusExpansionLink(targetUrl.href);
    };
    t3 = [radiusMeters];
    $[0] = radiusMeters;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== radiusExpansionLink || $[4] !== radiusMeters) {
    t4 = radiusMeters < 10000 ? <><p className={classNames(ownClasses["notification-description"], "body-text small")}>Espandi la tua ricerca e modifica il raggio per consultare gli annunci disponibili</p><ButtonLink design="solid" href={radiusExpansionLink} classes={[ownClasses["radius-expander"]]}>{`Cerca entro ${formatDistance(nextRadiusStep(radiusMeters))}`}</ButtonLink></> : <p className={classNames(ownClasses["notification-description"], "body-text small")}>Cambiare la query o il centro della tua ricerca per consultare gli annunci disponibili</p>;
    $[3] = radiusExpansionLink;
    $[4] = radiusMeters;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export function ZeroResultsComponent(props) {
  const $ = _c(49);
  const {
    query,
    category,
    geo,
    searchNearRegions,
    adType,
    radiusSearch
  } = props.search;
  const isRadiusSearch = radiusSearch != null;
  let t0;
  if ($[0] !== geo || $[1] !== searchNearRegions) {
    t0 = getLabelFromGeo(geo, searchNearRegions);
    $[0] = geo;
    $[1] = searchNearRegions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const geoLabel = t0;
  const geoLevel = getGeoLevel(geo, searchNearRegions);
  const enableExpandSearch = geoLevel !== GeoLevel.Italia;
  let t1;
  if ($[3] !== props.search.filters) {
    t1 = Object.keys(props.search.filters);
    $[3] = props.search.filters;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const filtersCount = t1.length;
  const t2 = adTypesLabelsMap[adType] || "";
  let t3;
  if ($[5] !== t2) {
    t3 = t2.toLowerCase();
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const adTypeLabel = t3;
  const enableSwitchToSellAdType = adType === AdTypes.GiveAway;
  const {
    isMobile
  } = useViewport();
  const {
    search,
    filtersConfig
  } = props;
  const savedSearchesProvider = useSavedSearches();
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  let t4;
  if ($[7] !== filtersConfig || $[8] !== savedSearchesProvider || $[9] !== search) {
    t4 = e => {
      e.preventDefault();
      trackSaveSearchButton();
      savedSearchesProvider.saveSearch(search, filtersConfig);
    };
    $[7] = filtersConfig;
    $[8] = savedSearchesProvider;
    $[9] = search;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleSave = t4;
  let t5;
  if ($[11] !== optimizely) {
    t5 = () => {
      optimizely?.onReady().then(() => {
        optimizely.track("subito_listing_zero_results");
      });
    };
    $[11] = optimizely;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== category.id || $[14] !== optimizely) {
    t6 = [category.id, optimizely];
    $[13] = category.id;
    $[14] = optimizely;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  const t7 = "Nessun risultato al momento...";
  const t8 = ownClasses["filters-description"];
  let t9;
  if ($[16] !== query) {
    t9 = query && <><span className="body-text small">{"Stai cercando "}</span><span className="caption">{`${query} `}</span><span className="body-text small">{"in "}</span></>;
    $[16] = query;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = `${category.label} ${adTypeLabel} `;
  let t11;
  if ($[18] !== t10) {
    t11 = <span className="caption">{t10}</span>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = !isRadiusSearch ? <><span className="body-text small">{getGeoInclusionLabel(geoLevel ?? GeoLevel.Italia)}</span><span className="caption">{` ${geoLabel}`}</span>.</> : <><span className="body-text small">a</span><span className="caption">{` ${radiusSearch?.friendlyName}`}</span><span>.</span></>;
  let t13;
  if ($[20] !== filtersCount) {
    t13 = filtersCount > 0 && <span className="body-text small">{` Hai anche altri ${filtersCount} filtri attivi.`}</span>;
    $[20] = filtersCount;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t8 || $[26] !== t9) {
    t14 = <div className={t8}>{t9}{t11}{t12}{t13}</div>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t8;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== handleSave || $[29] !== isRadiusSearch || $[30] !== radiusSearch) {
    t15 = !isRadiusSearch ? <><p className={classNames("body-text", "book", "small", ownClasses["notification-description"])}>Salva la ricerca e ti aggiorneremo non appena verranno inseriti nuovi annunci.</p><div className={ownClasses["save-button-container"]}><Button design="solid" onClick={handleSave}>Avvisami</Button></div></> : <RadiusSearchExpansion search={radiusSearch} />;
    $[28] = handleSave;
    $[29] = isRadiusSearch;
    $[30] = radiusSearch;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== enableExpandSearch) {
    t16 = enableExpandSearch && <><p className="body-text small">oppure</p><ExpandSearch withContainer={false} /></>;
    $[32] = enableExpandSearch;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== enableSwitchToSellAdType || $[35] !== props) {
    t17 = enableSwitchToSellAdType && <><p className={classNames("body-text", "book", "small", ownClasses["sell-cta"])}>Non trovi quello che fa per te? Non lasciarti scappare un buon affare</p><Button design="text" size="small" onClick={() => props.updateAdType(AdTypes.Sell)}>Cerca annunci in vendita</Button></>;
    $[34] = enableSwitchToSellAdType;
    $[35] = props;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== isMobile) {
    t18 = isMobile && <div id={LiraContainerIds.listing.box3} />;
    $[37] = isMobile;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <AdsZeroPageBlock cssClass={ownClasses["adsense-container"]} />;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t17 || $[44] !== t18) {
    t20 = <div>{t14}{t15}{t16}{t17}{t18}{t19}</div>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ZrpImage className={ownClasses.illustration} />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t20) {
    const data = {
      title: t7,
      message: t20,
      image: t21
    };
    t22 = <ErrorLayout {...data} />;
    $[47] = t20;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  return t22;
}
export const ZeroResults = connect<ZRStateProps, ZRDispatchProps, unknown, RootState>(state => ({
  search: state.search,
  filtersConfig: state.filtersConfig
}), dispatch => ({
  updateAdType: type => dispatch(updateAdType(type))
}))(ZeroResultsComponent);